import React, { useMemo, useContext, createContext, useState, useCallback, useEffect } from 'react';
import { documentsClient } from 'clients';
import { DocumentUploadCategory, PropertyDocument } from 'types/Documents';
import { DocumentCategories, getDocumentCategories } from 'utils/documents';

interface DocumentsContextType {
    documents: PropertyDocument[] | null;
    categories: DocumentUploadCategory[];
    documentCategories: DocumentCategories;
    loading: boolean;
    errorMessage: string;
    getDocuments: (spotUuid: string) => Promise<void>;
    uploadDocuments: (spotUuid: string, files: File[], category: DocumentUploadCategory) => Promise<void>;
}

export const DocumentsContext = createContext<DocumentsContextType | null>(null);

export const DocumentsProvider: React.FunctionComponent<React.PropsWithChildren<object>> = ({ children }) => {
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [documents, setDocuments] = useState<PropertyDocument[] | null>(null);
    const [categories, setCategories] = useState<DocumentUploadCategory[]>([]);

    const getCategories = useCallback(async () => {
        const result = await documentsClient.getCategories();
        setCategories(result);
    }, []);

    useEffect(() => {
        getCategories();
    }, [getCategories]);

    const getDocuments = useCallback(async (spotUuid: string) => {
        setLoading(true);
        try {
            const result = await documentsClient.getDocuments(spotUuid);
            setDocuments(result);
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Something went wrong');
        } finally {
            setLoading(false);
        }
    }, []);

    const uploadDocuments = useCallback(async (spotUuid: string, files: File[], category: DocumentUploadCategory) => {
        setLoading(true);
        try {
            await documentsClient.uploadDocuments(spotUuid, files, category.id);
            const updatedDocuments = await documentsClient.getDocuments(spotUuid);
            setDocuments(updatedDocuments);
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Something went wrong');
        } finally {
            setLoading(false);
        }
    }, []);

    const documentCategories = useMemo(() => getDocumentCategories(categories), [categories]);

    const value = useMemo<DocumentsContextType>(() => {
        return {
            documents,
            categories,
            documentCategories,
            loading,
            errorMessage,
            getDocuments,
            uploadDocuments,
        };
    }, [documents, categories, documentCategories, loading, errorMessage, getDocuments, uploadDocuments]);

    return <DocumentsContext.Provider value={value}>{children}</DocumentsContext.Provider>;
};

export const useDocuments = () => {
    const context = useContext(DocumentsContext);

    if (!context) {
        throw new Error('Error: useDocuments should be wrapped by DocumentsProvider.');
    }
    return context;
};
